import { createFeature, createReducer, on } from '@ngrx/store';
import { Bank, OnboardingData } from '@zero/shared/util-models';
import { Branch } from '../entities/branch';
import { Country } from '../entities/country.entity';
import { onboardingActions, onboardingApiActions } from './onboarding.actions';

export interface OnboardingState {
  step: number;
  doStep: boolean;
  email: string;
  sendingEmail: boolean;
  sentEmailError: string;
  sentEmailSuccessfully: boolean;
  sendingPassword: boolean;
  sentPasswordError: string;
  sentPasswordSuccessfully: boolean;
  sendingConfirmationData: boolean;
  sentConfirmationDataSuccessfully: boolean;
  sentConfirmationDataError: string;
  loginSuccess: boolean;
  loggingIn: boolean;
  loginErrorMessage: string;
  onboardingData: OnboardingData;
  onboardingDataLoading: boolean;
  sendingPhoneNumber: boolean;
  sentPhoneNumberSuccessfully: boolean;
  isPhoneNumberVerified: boolean;
  isPhoneNumberVerifiedLoading: boolean;
  verifyingCode: boolean;
  sentCodeSuccessfully: boolean;
  resendCodeInProgress: boolean;
  resendCodeError: string;
  sendingInitialDeposit: boolean;
  doInitialDepositLater: boolean;
  sentInitialDepositSuccessfully: boolean;
  sendingPersonalInformation: boolean;
  sentPersonalInformationSuccessfully: boolean;
  sendingResidenceInformation: boolean;
  sentResidenceInformationSuccessfully: boolean;
  bankData: Bank;
  sendingBankAccountInformation: boolean;
  sentBankAccountInformationSuccessfully: boolean;
  branches: Branch[];
  sendingNationalityTaxAndEmploymentInformation: boolean;
  sentNationalityTaxAndEmploymentInformationSuccessfully: boolean;
  sendingProvisionInformation: boolean;
  sentProvisionInformationSuccessfully: boolean;
  error: string;
  countries: Country[];
  deletingAllData: boolean;
  deleteAllDataSuccessfully: boolean;
}

const initialState: OnboardingState = {
  step: 1,
  doStep: false,
  email: null,
  sendingEmail: false,
  sentEmailError: null,
  sentEmailSuccessfully: false,
  sendingPassword: false,
  sentPasswordError: null,
  sentPasswordSuccessfully: false,
  sendingConfirmationData: false,
  sentConfirmationDataSuccessfully: false,
  sentConfirmationDataError: null,
  loginSuccess: false,
  loggingIn: false,
  loginErrorMessage: null,
  onboardingData: null,
  onboardingDataLoading: false,
  sendingPhoneNumber: null,
  sentPhoneNumberSuccessfully: null,
  isPhoneNumberVerified: false,
  isPhoneNumberVerifiedLoading: false,
  verifyingCode: null,
  sentCodeSuccessfully: null,
  resendCodeInProgress: false,
  resendCodeError: null,
  sendingInitialDeposit: null,
  doInitialDepositLater: null,
  sentInitialDepositSuccessfully: null,
  sendingPersonalInformation: null,
  sentPersonalInformationSuccessfully: null,
  sendingResidenceInformation: null,
  sentResidenceInformationSuccessfully: null,
  bankData: null,
  sendingBankAccountInformation: null,
  sentBankAccountInformationSuccessfully: null,
  branches: null,
  sendingNationalityTaxAndEmploymentInformation: null,
  sentNationalityTaxAndEmploymentInformationSuccessfully: null,
  sendingProvisionInformation: null,
  sentProvisionInformationSuccessfully: null,
  error: null,
  countries: [],
  deletingAllData: false,
  deleteAllDataSuccessfully: false,
};

export const onboardingFeature = createFeature({
  name: 'onboarding',
  reducer: createReducer(
    initialState,
    on(
      onboardingActions.setStep,
      (state, { step }): OnboardingState => ({
        ...state,
        step,
        doStep: false,
      }),
    ),
    on(
      onboardingActions.goToStep,
      (state, { step }): OnboardingState => ({
        ...state,
        step,
        doStep: true,
      }),
    ),
    on(
      onboardingActions.goToNextStep,
      (state): OnboardingState => ({
        ...state,
        step: state.step + 1,
        doStep: true,
        sentEmailSuccessfully: false,
        sentPasswordSuccessfully: false,
        sentPhoneNumberSuccessfully: false,
        sentCodeSuccessfully: false,
        sentInitialDepositSuccessfully: false,
        sentPersonalInformationSuccessfully: false,
        sentResidenceInformationSuccessfully: false,
        sentBankAccountInformationSuccessfully: false,
        sentNationalityTaxAndEmploymentInformationSuccessfully: false,
        sentProvisionInformationSuccessfully: false,
        deleteAllDataSuccessfully: false,
        error: null,
      }),
    ),
    on(
      onboardingActions.goToPreviousStep,
      (state): OnboardingState => ({
        ...state,
        step: state.step - 1,
        doStep: true,
        error: null,
      }),
    ),
    on(
      onboardingActions.setStepDone,
      (state): OnboardingState => ({
        ...state,
        doStep: false,
      }),
    ),
    on(
      onboardingActions.sendEmail,
      (state): OnboardingState => ({
        ...state,
        sendingEmail: true,
      }),
    ),
    on(
      onboardingApiActions.sendEmailSuccess,
      (state, { email }): OnboardingState => ({
        ...state,
        email,
        sendingEmail: false,
        sentEmailError: null,
        sentEmailSuccessfully: true,
      }),
    ),
    on(
      onboardingApiActions.sendEmailFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        sendingEmail: false,
        sentEmailError: error,
        sentEmailSuccessfully: false,
      }),
    ),
    on(
      onboardingActions.signUp,
      (state): OnboardingState => ({
        ...state,
        sendingPassword: true,
        sentPasswordError: null,
      }),
    ),
    on(
      onboardingApiActions.signUpSuccess,
      (state): OnboardingState => ({
        ...state,
        sendingPassword: false,
        sentPasswordError: null,
        sentPasswordSuccessfully: true,
      }),
    ),
    on(
      onboardingApiActions.signUpFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        sendingPassword: false,
        sentPasswordError: error,
        sentPasswordSuccessfully: false,
      }),
    ),
    on(
      onboardingActions.sendConfirmationData,
      (state): OnboardingState => ({
        ...state,
        sendingConfirmationData: true,
      }),
    ),
    on(
      onboardingApiActions.sendConfirmationDataSuccess,
      (state): OnboardingState => ({
        ...state,
        sendingConfirmationData: false,
        sentConfirmationDataSuccessfully: true,
      }),
    ),
    on(
      onboardingApiActions.sendConfirmationDataFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        sentConfirmationDataError: error,
      }),
    ),
    on(
      onboardingActions.login,
      (state): OnboardingState => ({
        ...state,
        loggingIn: true,
        loginSuccess: false,
        loginErrorMessage: null,
      }),
    ),
    on(
      onboardingApiActions.loginSuccess,
      (state): OnboardingState => ({
        ...state,
        loggingIn: false,
        loginSuccess: true,
        loginErrorMessage: null,
      }),
    ),
    on(
      onboardingApiActions.loginFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        loggingIn: false,
        loginErrorMessage: error,
      }),
    ),
    on(
      onboardingActions.loadOnboardingData,
      (state): OnboardingState => ({
        ...state,
        onboardingDataLoading: true,
        onboardingData: null,
      }),
    ),
    on(
      onboardingApiActions.loadOnboardingDataSuccess,
      (state, { onboardingData }): OnboardingState => ({
        ...state,
        onboardingDataLoading: false,
        onboardingData,
      }),
    ),
    on(
      onboardingApiActions.loadOnboardingDataFailure,
      (state): OnboardingState => ({
        ...state,
        onboardingDataLoading: false,
      }),
    ),
    on(
      onboardingActions.sendPhoneNumber,
      (state): OnboardingState => ({
        ...state,
        sendingPhoneNumber: true,
      }),
    ),
    on(
      onboardingApiActions.sendPhoneNumberSuccess,
      (state, { onboardingData }): OnboardingState => ({
        ...state,
        onboardingData,
        sendingPhoneNumber: false,
        sentPhoneNumberSuccessfully: true,
      }),
    ),
    on(
      onboardingApiActions.sendPhoneNumberFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        error,
        sendingPhoneNumber: false,
        sentPhoneNumberSuccessfully: false,
      }),
    ),
    on(
      onboardingActions.checkPhoneNumberVerified,
      (state): OnboardingState => ({
        ...state,
        isPhoneNumberVerified: false,
        isPhoneNumberVerifiedLoading: true,
      }),
    ),
    on(
      onboardingApiActions.checkPhoneNumberVerifiedSuccess,
      (state, { isPhoneNumberVerified }): OnboardingState => ({
        ...state,
        isPhoneNumberVerified,
        isPhoneNumberVerifiedLoading: false,
        error: null,
      }),
    ),
    on(
      onboardingApiActions.checkPhoneNumberVerifiedFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        isPhoneNumberVerifiedLoading: false,
        error,
      }),
    ),
    on(onboardingActions.clearPhoneNumberError, (state): OnboardingState => ({ ...state, error: null })),
    on(
      onboardingActions.verifyCode,
      (state): OnboardingState => ({
        ...state,
        verifyingCode: true,
      }),
    ),
    on(
      onboardingApiActions.verifyCodeSuccess,
      (state): OnboardingState => ({
        ...state,
        verifyingCode: false,
        sentCodeSuccessfully: true,
        isPhoneNumberVerified: true,
      }),
    ),
    on(
      onboardingApiActions.verifyCodeFailure,
      (state): OnboardingState => ({
        ...state,
        verifyingCode: false,
        sentCodeSuccessfully: false,
        isPhoneNumberVerified: false,
      }),
    ),
    on(
      onboardingActions.resetSentCodeSuccessfully,
      (state): OnboardingState => ({
        ...state,
        sentCodeSuccessfully: null,
      }),
    ),
    on(
      onboardingActions.resendCode,
      (state): OnboardingState => ({
        ...state,
        resendCodeInProgress: true,
        resendCodeError: null,
      }),
    ),
    on(
      onboardingApiActions.resendCodeSuccess,
      (state): OnboardingState => ({
        ...state,
        resendCodeInProgress: false,
      }),
    ),
    on(
      onboardingApiActions.resendCodeFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        resendCodeInProgress: false,
        resendCodeError: error,
      }),
    ),
    on(
      onboardingActions.sendInitialDeposit,
      (state): OnboardingState => ({
        ...state,
        sendingInitialDeposit: true,
      }),
    ),
    on(
      onboardingActions.doInitialDepositLater,
      (state): OnboardingState => ({
        ...state,
        doInitialDepositLater: true,
      }),
    ),
    on(
      onboardingApiActions.sendInitialDepositSuccess,
      (state, { onboardingData }): OnboardingState => ({
        ...state,
        onboardingData,
        sendingInitialDeposit: false,
        sentInitialDepositSuccessfully: true,
      }),
    ),
    on(
      onboardingApiActions.sendInitialDepositFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        sendingInitialDeposit: false,
        sentInitialDepositSuccessfully: false,
        error,
      }),
    ),
    on(
      onboardingActions.sendPersonalInformation,
      (state): OnboardingState => ({
        ...state,
        sendingPersonalInformation: true,
      }),
    ),
    on(
      onboardingApiActions.sendPersonalInformationSuccess,
      (state, { onboardingData }): OnboardingState => ({
        ...state,
        onboardingData,
        sendingPersonalInformation: false,
        sentPersonalInformationSuccessfully: true,
      }),
    ),
    on(
      onboardingApiActions.sendPersonalInformationFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        sendingPersonalInformation: false,
        sentPersonalInformationSuccessfully: false,
        error,
      }),
    ),
    on(
      onboardingActions.sendResidenceInformation,
      (state): OnboardingState => ({
        ...state,
        sendingResidenceInformation: true,
      }),
    ),
    on(
      onboardingApiActions.sendResidenceInformationSuccess,
      (state, { onboardingData }): OnboardingState => ({
        ...state,
        onboardingData,
        sendingResidenceInformation: false,
        sentResidenceInformationSuccessfully: true,
      }),
    ),
    on(
      onboardingApiActions.sendResidenceInformationFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        sendingResidenceInformation: false,
        sentResidenceInformationSuccessfully: false,
        error,
      }),
    ),
    on(
      onboardingApiActions.loadBankByIbanSuccess,
      (state, { bank }): OnboardingState => ({
        ...state,
        bankData: bank,
        error: null,
      }),
    ),
    on(
      onboardingApiActions.loadBankByIbanFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        error,
      }),
    ),
    on(
      onboardingActions.clearBankData,
      (state): OnboardingState => ({
        ...state,
        bankData: null,
      }),
    ),
    on(
      onboardingActions.sendBankAccountInformation,
      (state): OnboardingState => ({
        ...state,
        sendingBankAccountInformation: true,
      }),
    ),
    on(
      onboardingApiActions.sendBankAccountInformationSuccess,
      (state, { onboardingData }): OnboardingState => ({
        ...state,
        onboardingData,
        sendingBankAccountInformation: false,
        sentBankAccountInformationSuccessfully: true,
      }),
    ),
    on(
      onboardingApiActions.sendBankAccountInformationFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        sendingBankAccountInformation: false,
        sentBankAccountInformationSuccessfully: false,
        error,
      }),
    ),
    on(
      onboardingApiActions.loadBranchesSuccess,
      (state, { branches }): OnboardingState => ({
        ...state,
        branches,
      }),
    ),
    on(
      onboardingActions.sendNationalityTaxAndEmploymentInformation,
      (state): OnboardingState => ({
        ...state,
        sendingNationalityTaxAndEmploymentInformation: true,
      }),
    ),
    on(
      onboardingApiActions.sendNationalityTaxAndEmploymentInformationSuccess,
      (state, { onboardingData }): OnboardingState => ({
        ...state,
        onboardingData,
        sendingNationalityTaxAndEmploymentInformation: false,
        sentNationalityTaxAndEmploymentInformationSuccessfully: true,
      }),
    ),
    on(
      onboardingApiActions.sendNationalityTaxAndEmploymentInformationFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        sendingNationalityTaxAndEmploymentInformation: false,
        sentNationalityTaxAndEmploymentInformationSuccessfully: false,
        error,
      }),
    ),
    on(
      onboardingActions.sendProvisionInformation,
      (state): OnboardingState => ({
        ...state,
        sendingProvisionInformation: true,
      }),
    ),
    on(
      onboardingApiActions.sendProvisionInformationSuccess,
      (state, { onboardingData }): OnboardingState => ({
        ...state,
        onboardingData,
        sendingProvisionInformation: false,
        sentProvisionInformationSuccessfully: true,
      }),
    ),
    on(
      onboardingApiActions.sendProvisionInformationFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        sendingProvisionInformation: false,
        sentProvisionInformationSuccessfully: false,
        error,
      }),
    ),
    on(
      onboardingApiActions.loadCountriesSuccess,
      (state, { countries }): OnboardingState => ({
        ...state,
        countries,
      }),
    ),
    on(
      onboardingActions.deleteAllData,
      (state): OnboardingState => ({
        ...state,
        deletingAllData: true,
      }),
    ),
    on(
      onboardingApiActions.deleteAllDataSuccess,
      (state): OnboardingState => ({
        ...state,
        deletingAllData: false,
        deleteAllDataSuccessfully: true,
      }),
    ),
    on(
      onboardingApiActions.deleteAllDataFailure,
      (state, { error }): OnboardingState => ({
        ...state,
        deletingAllData: false,
        deleteAllDataSuccessfully: false,
        error,
      }),
    ),
  ),
});
